// Synthetic column keys injected client-side for the B2B revenue table.
// These are not sent by the backend — the FE derives them from the circular fields.
export const CIRCULAR_TXN_KEY = '__circularTxn';
export const NET_KEY = '__net';

// Backend key of the Total column (revenue table) — relabelled to "Total" + made sortable.
// (Same literal the overlap indicator used to key off — one source now.)
export const TOTAL_COLUMN_KEY = 'totalRevenue';

// Backend-sent summary rows are matched by name (their circular % renders as plain
// text, not a pill badge, per the design).
export const SUMMARY_ROW_NAMES = ['Total Revenue', 'Total B2B Revenue', 'Total Expense', 'Net B2B Revenue', 'Intra Company Txn.'];

// Polus (issue #670) serializes isOverlapping as the string "true", not a boolean.
// Accept both forms so the gate stays correct if the serialization is ever fixed upstream.
export const OVERLAP_FLAG_STRING = 'true' as const;

// How long the tooltip stays visible after the cursor leaves the trigger (ms)
export const OVERLAP_TOOLTIP_HIDE_DELAY = 400;

export interface OverlapRow {
    isOverlapping?: string | boolean;
    circularValue?: string;
    circularPct?: string;
    circularTotal?: string;
    totalRevenue?: string;
    [key: string]: unknown;
}

/**
 * Gate — returns true when the row is flagged as overlapping.
 * Polus (#670) sends isOverlapping as the string "true"; accept a real boolean too.
 */
export function isOverlappingRow(row: OverlapRow): boolean {
    return row?.isOverlapping === OVERLAP_FLAG_STRING || row?.isOverlapping === true;
}

// Tooltip copy for the column-header info icons.
export const CIRCULAR_TXN_TOOLTIP = 'This party is customer as well vendor';
export const NET_COLUMN_TOOLTIP = 'Net revenue from this party';
export const ROW_TOOLTIPS: Record<string, string> = {
    'Net B2B Revenue': 'This is net B2B revenue after subtracting Intra Company Txn.',
    'Intra Company Txn.': "This amount is billed for the same company's GST number",
};

/**
 * Circular % → severity bucket (product-defined):
 * ≤10 yellow · >10–30 orange · >30–50 light red · >50 dark red.
 */
export function circularSeverity(pct?: string): 'yellow' | 'orange' | 'lightRed' | 'darkRed' {
    const n = pct ? parseFloat(String(pct)) : 0;
    if (n <= 10) return 'yellow';
    if (n <= 30) return 'orange';
    if (n <= 50) return 'lightRed';
    return 'darkRed';
}

/** Ensure the circular % renders with a trailing "%" (backend sends the number without it). */
export function formatCircularPct(pct?: string): string {
    if (pct === undefined || pct === null || pct === '') return '';
    const s = String(pct).trim();
    return s.endsWith('%') ? s : `${s}%`;
}

/** Parse a "₹12,34,567" string to a number. */
export function parseINR(value: string | undefined): number {
    if (!value) return 0;
    return parseFloat(value.replace(/[₹,]/g, '')) || 0;
}

/** Format a number as "₹12,34,567" (Indian grouping, no fraction). */
export function formatINR(value: number): string {
    return '₹' + value.toLocaleString('en-IN');
}

/**
 * True when a table cell holds a NEGATIVE rupee amount.
 * Gated on '₹' so non-amount columns (state, names, % numbers) can never match, and
 * routed through parseINR so the empty-cell placeholder '-' parses to 0, not a negative
 * (`parseFloat('-')` → NaN → `|| 0`). A naive startsWith('-')/includes('-') is inverted
 * for this table: our negatives read "₹-12,03,750" (minus AFTER the ₹) while '-' means EMPTY.
 */
export function isNegativeAmount(value: unknown): boolean {
    if (typeof value !== 'string' || !value.includes('₹')) return false;
    return parseINR(value) < 0;
}

/** True when the row is a backend-sent summary row (Total Revenue, Net B2B Revenue, …). */
export function isSummaryRow(row: OverlapRow): boolean {
    return typeof row?.name === 'string' && SUMMARY_ROW_NAMES.includes(row.name as string);
}

/**
 * Net = Total − Circular, keyed off the row's DISPLAYED total (totalRevenue), not circularTotal.
 * circularTotal is max(rev, exp) upstream — for a party whose expense exceeds its revenue that
 * would render exp − rev (which can exceed the displayed Total); revenue − circular correctly
 * yields 0 when the entire revenue is circular. Returns '-' when the row carries no circular data.
 */
export function getNetValue(row: OverlapRow): string {
    if (!isOverlappingRow(row) || !row?.totalRevenue || !row?.circularValue) return '-';
    return formatINR(parseINR(row.totalRevenue) - parseINR(row.circularValue));
}

/**
 * Circular % of the row's DISPLAYED total (circularValue / totalRevenue) — the single
 * denominator the badge, severity colour, sort, and tooltip all share. The backend's
 * circularPct is min/max-based, which diverges from the displayed Total when a party's
 * expense exceeds its revenue (badge said 40% while the tooltip's amounts implied 100%);
 * revenue-based reads as "share of this party's revenue that is circular" and is ≤100 by
 * construction (circularValue = min(rev, exp) ≤ revenue). Falls back to the backend
 * circularPct when the totals needed to recompute are absent.
 */
export function circularRevenuePct(row: OverlapRow): string | undefined {
    if (!isOverlappingRow(row)) return undefined;
    const total = parseINR(row.totalRevenue);
    const circ = parseINR(row.circularValue);
    if (!total || !circ) return row?.circularPct;
    return String(Math.round((circ / total) * 1000) / 10);
}

// Optional row key on GST "Vendor Expense" rows: the vendor PAN when it is in the org's latest MSME delay period.
export const MSME_DELAY_PAN_KEY = 'msmeDelayPan';
export const MSME_DELAY_TAG_LABEL = 'MSME Delay';
export const msmeDelayTagAriaLabel = (vendorName: string) => `${MSME_DELAY_TAG_LABEL} — ${vendorName}`;
export const MSME_DELAY_LOAD_ERROR = 'Unable to load MSME delay details. Please try again.';
